import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/use-organization";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { Coffee, Dumbbell } from "lucide-react";
import { format } from "date-fns";
import { getDefaultStartDate } from "@/lib/next-monday";
import { StartDateField } from "@/components/programs/StartDateField";
import {
  ReplaceProgramDialog,
  type ActiveAssignmentSummary,
} from "@/components/programs/ReplaceProgramDialog";

interface AssignProgramDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  clientId: string;
  clientName: string;
}

export function AssignProgramDialog({
  open,
  onOpenChange,
  onSuccess,
  clientId,
  clientName,
}: AssignProgramDialogProps) {
  const { organization } = useOrganization();
  const [selectedProgramId, setSelectedProgramId] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [startDate, setStartDate] = useState<string>(() =>
    format(getDefaultStartDate(), "yyyy-MM-dd"),
  );
  // Conflict-confirm state. Populated when handleAssign detects the client
  // already has a different active program.
  const [pendingReplace, setPendingReplace] = useState<ActiveAssignmentSummary | null>(null);

  // Reset start date + selection each time the dialog re-opens so we never
  // leak stale picks from a prior client.
  useEffect(() => {
    if (open) {
      setStartDate(format(getDefaultStartDate(), "yyyy-MM-dd"));
      setSelectedProgramId("");
      setPendingReplace(null);
    }
  }, [open]);

  // Fetch available programs
  const { data: programs = [] } = useQuery({
    queryKey: ["programs-for-assignment", organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];

      const { data, error } = await supabase
        .from("programs")
        .select(`
          id,
          name,
          description,
          days_per_cycle,
          intended_cycles,
          program_days (
            id,
            day_number,
            name,
            is_rest_day,
            workout_template_id
          )
        `)
        .eq("organization_id", organization.id)
        .order("name");

      if (error) throw error;
      return data || [];
    },
    enabled: open && !!organization?.id,
  });

  // Fetch the client's CURRENT active assignment (if any) so we can detect
  // a conflict before inserting. Single query — RLS scopes to the trainer
  // already.
  const { data: existingAssignment } = useQuery({
    queryKey: ["active-assignment-for-client", clientId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("client_program_assignments")
        .select(`
          id,
          program_id,
          started_on,
          current_day,
          programs:program_id ( name, days_per_cycle, intended_cycles )
        `)
        .eq("client_id", clientId)
        .eq("status", "active")
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: open && !!clientId,
  });

  const selectedProgram = programs.find((p) => p.id === selectedProgramId);

  /**
   * Performs the actual write: optional replace of existing + insert new.
   * Wrapped because both the no-conflict path and the post-confirm path
   * call into the same logic.
   */
  const writeAssignment = async () => {
    setSaving(true);
    try {
      // If we have a different active program, mark it 'replaced'.
      if (
        existingAssignment &&
        existingAssignment.program_id !== selectedProgramId
      ) {
        const { error: updateError } = await supabase
          .from("client_program_assignments")
          .update({ status: "replaced", updated_at: new Date().toISOString() })
          .eq("id", existingAssignment.id);
        if (updateError) throw updateError;
      }

      const { error } = await supabase
        .from("client_program_assignments")
        .insert({
          client_id: clientId,
          program_id: selectedProgramId,
          status: "active",
          current_day: 1,
          started_on: startDate,
        });

      if (error) throw error;

      toast.success("Program assigned");
      onSuccess();
      onOpenChange(false);
      setSelectedProgramId("");
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : "Failed to assign program";
      console.error("Error assigning program:", error);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedProgramId) {
      toast.error("Please select a program");
      return;
    }
    if (!startDate) {
      toast.error("Please pick a start date");
      return;
    }

    // No-op assign (same program already active) → just close, no replace
    // dialog, no duplicate insert.
    if (
      existingAssignment &&
      existingAssignment.program_id === selectedProgramId
    ) {
      toast.info(`${clientName} is already on this program`);
      onOpenChange(false);
      return;
    }

    // Different-program conflict → surface ReplaceProgramDialog.
    if (
      existingAssignment &&
      existingAssignment.program_id !== selectedProgramId
    ) {
      const program = Array.isArray(existingAssignment.programs)
        ? existingAssignment.programs[0]
        : existingAssignment.programs;

      // Fetch the last workout log for the "X days ago" line. Failure-soft:
      // if this query errors we drop the fragment instead of blocking.
      let lastWorkoutAt: string | null = null;
      try {
        const { data: lastLog } = await supabase
          .from("workout_logs")
          .select("completed_at")
          .eq("client_id", clientId)
          .not("completed_at", "is", null)
          .order("completed_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        lastWorkoutAt = lastLog?.completed_at ?? null;
      } catch (e) {
        console.warn("[AssignProgramDialog] last-workout lookup failed", e);
      }

      setPendingReplace({
        assignmentId: existingAssignment.id,
        programName: program?.name ?? "their current program",
        startedOn: existingAssignment.started_on,
        currentDay: existingAssignment.current_day,
        daysPerCycle: program?.days_per_cycle ?? 7,
        intendedCycles: program?.intended_cycles ?? null,
        lastWorkoutAt,
      });
      return;
    }

    // No existing assignment — straight through.
    await writeAssignment();
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Assign Multi-Day Program</DialogTitle>
          <DialogDescription>
            Assign a program to {clientName}. This will cycle through the program days as they complete workouts.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Select Program</Label>
            <Select value={selectedProgramId} onValueChange={setSelectedProgramId}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a program..." />
              </SelectTrigger>
              <SelectContent>
                {programs.map((program) => {
                  // Pattern programs (intended_cycles set) span
                  // days_per_cycle × cycles total. Legacy/single-cycle
                  // programs are just days_per_cycle. Show the real total
                  // so a "4-week" program reads "28 days", not "7 days".
                  const cycles = (program as { intended_cycles?: number | null }).intended_cycles;
                  const totalDays =
                    cycles && cycles > 0 ? program.days_per_cycle * cycles : program.days_per_cycle;
                  const label =
                    cycles && cycles > 1
                      ? `${totalDays} days · ${cycles} weeks`
                      : `${totalDays} days`;
                  return (
                    <SelectItem key={program.id} value={program.id}>
                      {program.name} ({label})
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {programs.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No programs found. Create one first in the Programs section.
              </p>
            )}
          </div>

          <StartDateField value={startDate} onChange={setStartDate} disabled={saving} />

          {selectedProgram && (
            <div className="space-y-3">
              <Label>Program Preview</Label>
              <div className="border rounded-lg p-3 space-y-2 max-h-[200px] overflow-y-auto">
                {selectedProgram.program_days
                  ?.sort((a, b) => a.day_number - b.day_number)
                  .map((day) => (
                    <div key={day.id} className="flex items-center gap-2">
                      <Badge variant="outline" className="w-14 justify-center">
                        Day {day.day_number}
                      </Badge>
                      <span className="text-sm flex items-center gap-1.5">
                        {day.is_rest_day ? (
                          <>
                            <Coffee className="h-3.5 w-3.5 text-muted-foreground" />
                            Rest Day
                          </>
                        ) : (
                          <>
                            <Dumbbell className="h-3.5 w-3.5 text-primary" />
                            {day.name || day.workout_template?.name || "Workout Day"}
                          </>
                        )}
                      </span>
                    </div>
                  ))}
              </div>
              {selectedProgram.description && (
                <p className="text-sm text-muted-foreground">
                  {selectedProgram.description}
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleAssign} disabled={saving || !selectedProgramId}>
            {saving ? "Assigning..." : "Assign Program"}
          </Button>
        </DialogFooter>
      </DialogContent>
      {pendingReplace && (
        <ReplaceProgramDialog
          open={!!pendingReplace}
          onOpenChange={(o) => {
            if (!o) setPendingReplace(null);
          }}
          clientName={clientName}
          newProgramName={selectedProgram?.name ?? "this program"}
          current={pendingReplace}
          onConfirm={async () => {
            await writeAssignment();
            setPendingReplace(null);
          }}
        />
      )}
    </Dialog>
  );
}

export default AssignProgramDialog;
